import { useContext, useEffect, useRef, useState } from 'react';
import { HubConnection } from '@microsoft/signalr';
import { delay } from 'msw';
import frontEndMessageHub, { IFrontEndMessageHub } from '@utils-clean/front-end-message-hub';
import { UserContext } from '@contexts/user-context';

export interface MessageHubHookProps<TMessage extends CommentMessage = CommentMessage> {
    messageHubRelativeEndpoint: string;
    groupName: string | undefined;
    // Deleted messages go through the update queue to onCommentsUpdated, instead of straight to onCommentDeleted
    queueDeletes?: boolean;
    // Only messages this returns true for are handled, e.g. RiskIQ only needs those that change its counts
    messageFilter?: (message: TMessage) => boolean;
}

export interface MessageHubHandlers<TMessage extends CommentMessage = CommentMessage> {
    onCommentsUpdated: (messages: QueuedUpdate<TMessage>[]) => Promise<boolean>;
    // Not needed when deletes are queued
    onCommentDeleted?: (isSelfMessage: boolean, message: TMessage) => void;
}

export interface IMessageHubHook<TMessage extends CommentMessage = CommentMessage> {
    hubConnection: HubConnection;
    connectedAtLeastOnce: boolean;
    disconnect: () => void;
    startProcessingUpdates: () => void;
    subscribe: (handlers: MessageHubHandlers<TMessage>) => void;
}

// The hook that owns the connection (e.g. RiskIQ's), whose queue can also be managed by its owner
export interface IOwnedMessageHubHook<
    TMessage extends CommentMessage = CommentMessage,
> extends IMessageHubHook<TMessage> {
    // Adds updates to the queue that didn't come from the hub, so they're processed and retried the same way
    queueUpdates: (messages: QueuedUpdate<TMessage>[]) => void;
    // Drops queued updates, including any waiting to retry, e.g. when a full refresh makes them redundant
    clearQueuedUpdates: () => void;
}

export type CommentUpdateType =
    | 'CommentAdded'
    | 'CommentEdited'
    | 'CommentOpened'
    | 'CommentClosed'
    | 'ReplyAdded'
    | 'ReplyEdited'
    | 'ReplyDeleted';

// The fields every comment message has. Hubs that send more (e.g. RiskIQ) extend this
export interface CommentMessage {
    id: string;
    publisherUserId: string;
    // What changed, null for a deleted comment
    updateType: CommentUpdateType | null;
}

// A message in the update queue. Who published it and what changed are only needed when a message is received, so
// updates queued from outside the hub don't need them
export type QueuedUpdate<TMessage extends CommentMessage = CommentMessage> = Omit<
    TMessage,
    'publisherUserId' | 'updateType'
>;

// A connection owned higher up (e.g. by RiskIQ) whose group receives messages for more than one parent
export interface ExternalMessageHub<TMessage extends CommentMessage = CommentMessage> {
    hubConnection: HubConnection;
    connectedAtLeastOnce: boolean;
    messageFilter: (message: TMessage) => boolean;
}

export interface ExternalMessageHubHookProps<TMessage extends CommentMessage = CommentMessage> {
    externalMessageHub: ExternalMessageHub<TMessage>;
}

const useUpdateQueue = <TMessage extends CommentMessage>(
    onCommentsUpdated: (messages: QueuedUpdate<TMessage>[]) => Promise<boolean>,
) => {
    // Keyed by message id so a message queued more than once is only processed once
    const updateQueue = useRef<Map<string, QueuedUpdate<TMessage>>>(new Map());
    const updatesPaused = useRef<boolean>(true);
    const processingUpdates = useRef<boolean>(false);

    const processQueuedUpdatesRecursive = async () => {
        if (updatesPaused.current || !(updateQueue.current.size > 0)) {
            return;
        }

        const queuedMessages = [...updateQueue.current];
        updateQueue.current.clear();

        const success = await onCommentsUpdated(queuedMessages.map(([, message]) => message));

        if (!success) {
            updateQueue.current = new Map([...queuedMessages, ...updateQueue.current]);
            await delay(5000);
        }

        await processQueuedUpdatesRecursive();
    };

    const processQueuedUpdates = async () => {
        if (processingUpdates.current) {
            return;
        }

        processingUpdates.current = true;
        await processQueuedUpdatesRecursive();
        processingUpdates.current = false;
    };

    const startProcessingUpdates = () => {
        updatesPaused.current = false;

        // eslint-disable-next-line no-void
        void processQueuedUpdates();
    };

    const queueUpdates = (messages: QueuedUpdate<TMessage>[]) => {
        messages.forEach((message) => updateQueue.current.set(message.id, message));
        // eslint-disable-next-line no-void
        void processQueuedUpdates();
    };

    const clearQueuedUpdates = () => {
        updateQueue.current.clear();
    };

    return {
        updateQueue,
        updatesPaused,
        processQueuedUpdates,
        startProcessingUpdates,
        queueUpdates,
        clearQueuedUpdates,
    };
};

const useCommentsMessageHub = <TMessage extends CommentMessage = CommentMessage>({
    messageHubRelativeEndpoint,
    groupName,
    queueDeletes,
    messageFilter,
}: MessageHubHookProps<TMessage>): IOwnedMessageHubHook<TMessage> => {
    const { user } = useContext(UserContext);
    // Set by subscribe, the sidebar's handlers aren't available when this hook is created
    const handlersRef = useRef<MessageHubHandlers<TMessage>>(undefined);

    // Fixed for the lifetime of the component. Changing this requires remounting the comments sidebar
    const messageHubRef = useRef<IFrontEndMessageHub>(undefined);
    if (!messageHubRef.current) {
        messageHubRef.current = frontEndMessageHub(messageHubRelativeEndpoint);
    }
    const { hubConnection, connectToHub, disconnectFromHub } = messageHubRef.current;

    const connectRetryAbortController = useRef<AbortController>(new AbortController());

    const [connectedAtLeastOnce, setConnectedAtLeastOnce] = useState(false);
    const {
        updateQueue,
        updatesPaused,
        processQueuedUpdates,
        startProcessingUpdates,
        queueUpdates,
        clearQueuedUpdates,
    } = useUpdateQueue<TMessage>((messages) => handlersRef.current!.onCommentsUpdated(messages));

    const connectEndlessRetry = async (abortController: AbortController) => {
        if (abortController.signal.aborted) {
            return;
        }

        try {
            await connectToHub(groupName!);
            if (abortController.signal.aborted) {
                disconnectFromHub();
            }
            setConnectedAtLeastOnce(true);
        } catch {
            await delay(5000);
            await connectEndlessRetry(abortController);
        }
    };

    const disconnect = () => {
        hubConnection.off('CommentUpdated');
        hubConnection.off('CommentDeleted');
        updatesPaused.current = true;
        connectRetryAbortController.current.abort();
        disconnectFromHub();
    };

    useEffect(() => {
        if (groupName) {
            hubConnection.on('CommentUpdated', (data: TMessage) => {
                if (messageFilter && !messageFilter(data)) {
                    return;
                }

                updateQueue.current.set(data.id, data);
                // eslint-disable-next-line no-void
                void processQueuedUpdates();
            });

            hubConnection.on('CommentDeleted', (data: TMessage) => {
                if (messageFilter && !messageFilter(data)) {
                    return;
                }

                if (queueDeletes) {
                    updateQueue.current.set(data.id, data);
                    // eslint-disable-next-line no-void
                    void processQueuedUpdates();
                    return;
                }

                handlersRef.current?.onCommentDeleted?.(user?.id === data.publisherUserId, data);
            });

            // eslint-disable-next-line no-void
            void connectEndlessRetry(connectRetryAbortController.current);

            hubConnection.onreconnecting(() => connectRetryAbortController.current.abort());

            hubConnection.onreconnected(() => {
                connectRetryAbortController.current.abort();
                connectRetryAbortController.current = new AbortController();
                // eslint-disable-next-line no-void
                void connectEndlessRetry(connectRetryAbortController.current);
            });
        }

        return () => {
            disconnect();
        };
    }, [groupName]);

    const subscribe = (handlers: MessageHubHandlers<TMessage>) => {
        handlersRef.current = handlers;
    };

    return {
        hubConnection,
        connectedAtLeastOnce,
        disconnect,
        startProcessingUpdates,
        queueUpdates,
        clearQueuedUpdates,
        subscribe,
    };
};

// Listens on a connection owned higher up. Never joins groups or stops the connection, it only adds and removes its
// own handlers, so other listeners on the connection are unaffected
export const useExternalCommentsMessageHub = <TMessage extends CommentMessage = CommentMessage>({
    externalMessageHub,
}: ExternalMessageHubHookProps<TMessage>): IMessageHubHook<TMessage> => {
    const { hubConnection, connectedAtLeastOnce, messageFilter } = externalMessageHub;
    const { user } = useContext(UserContext);
    // Set by subscribe, the sidebar's handlers aren't available when this hook is created
    const handlersRef = useRef<MessageHubHandlers<TMessage>>(undefined);
    // off needs the exact handlers we passed to on, so we keep a function that removes them for disconnect to call
    const removeHandlersRef = useRef<() => void>(undefined);

    const { updateQueue, updatesPaused, processQueuedUpdates, startProcessingUpdates } = useUpdateQueue<TMessage>(
        (messages) => handlersRef.current!.onCommentsUpdated(messages),
    );

    const disconnect = () => {
        removeHandlersRef.current?.();
        updatesPaused.current = true;
    };

    // Added once on mount. The connection is already joined to its group, and messageFilter picks out this sidebar's
    // messages
    useEffect(() => {
        const onCommentUpdated = (data: TMessage) => {
            if (messageFilter(data)) {
                updateQueue.current.set(data.id, data);
                // eslint-disable-next-line no-void
                void processQueuedUpdates();
            }
        };

        const onCommentDeleted = (data: TMessage) => {
            if (messageFilter(data)) {
                handlersRef.current?.onCommentDeleted?.(user?.id === data.publisherUserId, data);
            }
        };

        hubConnection.on('CommentUpdated', onCommentUpdated);
        hubConnection.on('CommentDeleted', onCommentDeleted);

        removeHandlersRef.current = () => {
            hubConnection.off('CommentUpdated', onCommentUpdated);
            hubConnection.off('CommentDeleted', onCommentDeleted);
        };

        return () => {
            disconnect();
        };
    }, []);

    const subscribe = (handlers: MessageHubHandlers<TMessage>) => {
        handlersRef.current = handlers;
    };

    return { hubConnection, connectedAtLeastOnce, disconnect, startProcessingUpdates, subscribe };
};

export default useCommentsMessageHub;
